import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import getMonthsInRange from "../Month";
import { format } from "date-fns";
import useCompanyStore from "../../../../store/useCompanyStore";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Download, FileText, Table } from 'lucide-react';

const BalanceSheetTable = ({ data, onAccountClick, netProfitByPeriod = [] }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [balanceSheet, setBalanceSheet] = useState("Balance Sheet");
  const [showExportOptions, setShowExportOptions] = useState(false);
  const { companyInfo } = useCompanyStore();

  const categorizeAccounts = (accounts, type, category = null) => {
    return accounts.filter(
      (acc) =>
        acc.accountType === type &&
        (category ? acc.category === category : true)
    );
  };

  const calculateTotal = (accounts) => {
    return accounts.reduce((sum, acc) => sum + acc.balance, 0);
  };

  const handleAccountClick = (account) => {
    navigate("/TransactionRecord", {
      state: {
        accountName: account.accountName,
        accountType: account.accountOriginalType || account.accountType,
        accountCode: account.accountCode || "",
      },
    });
  };

  // Export to PDF function
  const exportToPDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;

    // Add company logo if available
    if (companyInfo?.logo) {
      try {
        const logoSize = 30; // Increased logo size
        const logoX = pageWidth - margin - logoSize;
        const logoY = 10;
        doc.addImage(companyInfo.logo, 'PNG', logoX, logoY, logoSize, logoSize);
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }

    // Report Title
    doc.setFontSize(14);
    doc.text(balanceSheet, pageWidth / 2, 35, { align: 'center' });

    // Date
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`As of: ${format(new Date(), "MMMM dd, yyyy")}`, pageWidth / 2, 42, { align: 'center' });

    // Prepare table data
    const tableData = [];

    // Process each major category
    const categories = [
      { label: "Assets", type: "Asset", hasSubcategories: true },
      { label: "Liabilities", type: "Liability", hasSubcategories: true },
      { label: "Equity", type: "Equity", hasSubcategories: false }
    ];

    categories.forEach(category => {
      // Category header
      tableData.push([
        { content: category.label.toUpperCase(), styles: { fontStyle: 'bold', fillColor: [59, 130, 246], textColor: [255, 255, 255] } },
        ...data.map(() => '')
      ]);

      if (category.hasSubcategories) {
        // For Assets and Liabilities with subcategories
        ["Current", "Non-Current"].forEach(subCategory => {
          const hasValues = data.some(dataset => {
            const accounts = categorizeAccounts(dataset.data, category.type, subCategory);
            return accounts.some(acc => acc.balance !== 0);
          });

          if (hasValues) {
            // Subcategory header
            tableData.push([
              { content: `${subCategory} ${category.label}`, styles: { fontStyle: 'bold', textColor: [59, 130, 246] } },
              ...data.map(() => '')
            ]);

            // Account rows
            const accounts = categorizeAccounts(data[0]?.data || [], category.type, subCategory)
              .filter(account => account.balance !== 0);

            accounts.forEach(account => {
              const row = [
                '  ' + account.accountName,
                ...data.map(dataset => {
                  const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
                  return matchingAccount ? matchingAccount.balance.toLocaleString() + ' Nu.' : '0 Nu.';
                })
              ];
              tableData.push(row);
            });

            // Subtotal row
            const subtotalRow = [
              { content: `Total ${subCategory} ${category.label}`, styles: { fontStyle: 'bold' } },
              ...data.map(dataset => {
                const accounts = categorizeAccounts(dataset.data, category.type, subCategory);
                return {
                  content: calculateTotal(accounts).toLocaleString() + ' Nu.',
                  styles: { fontStyle: 'bold' }
                };
              })
            ];
            tableData.push(subtotalRow);
          }
        });
      } else {
        // For Equity without subcategories
        const accounts = categorizeAccounts(data[0]?.data || [], category.type)
          .filter(account => account.balance !== 0);

        accounts.forEach(account => {
          const row = [
            '  ' + account.accountName,
            ...data.map(dataset => {
              const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
              return matchingAccount ? matchingAccount.balance.toLocaleString() + ' Nu.' : '0 Nu.';
            })
          ];
          tableData.push(row);
        });

        // Add Net Profit row under Equity
        if (category.type === "Equity" && netProfitByPeriod.length > 0) {
          tableData.push([
            { content: '  Net Profit (Retained Earnings)', styles: { fontStyle: 'bold', textColor: [34, 197, 94] } },
            ...netProfitByPeriod.map(np => ({
              content: np.toLocaleString() + ' Nu.',
              styles: { fontStyle: 'bold', textColor: np >= 0 ? [34, 197, 94] : [239, 68, 68] }
            }))
          ]);
        }
      }

      // Category total row
      const totalRow = [
        { content: `Total ${category.label}`, styles: { fontStyle: 'bold', fillColor: [219, 234, 254], textColor: [59, 130, 246] } },
        ...data.map((dataset, index) => {
          const accounts = categorizeAccounts(dataset.data, category.type);
          const accountTotal = calculateTotal(accounts);
          const netProfit = category.type === "Equity" ? (netProfitByPeriod[index] || 0) : 0;
          return {
            content: (accountTotal + netProfit).toLocaleString() + ' Nu.',
            styles: { fontStyle: 'bold', textColor: [59, 130, 246] }
          };
        })
      ];
      tableData.push(totalRow);
    });

    // Total Liabilities + Equity row
    const finalTotalRow = [
      { content: 'Total Liabilities + Equity', styles: { fontStyle: 'bold', fillColor: [59, 130, 246], textColor: [255, 255, 255] } },
      ...data.map((dataset, index) => {
        const liabilities = categorizeAccounts(dataset.data, "Liability");
        const equity = categorizeAccounts(dataset.data, "Equity");
        const netProfit = netProfitByPeriod[index] || 0;
        const total = calculateTotal(liabilities) + calculateTotal(equity) + netProfit;
        return {
          content: total.toLocaleString() + ' Nu.',
          styles: { fontStyle: 'bold', textColor: [255, 255, 255] }
        };
      })
    ];
    tableData.push(finalTotalRow);

    // Create table
    doc.autoTable({
      head: [['Account', ...data.map(dataset => getMonthsInRange(dataset.date))]],
      body: tableData,
      startY: 50,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'left' },
        ...Object.fromEntries(
          Array.from({ length: data.length }, (_, i) => [i + 1, { halign: 'right' }])
        )
      },
      didDrawPage: function (data) {
        // Footer
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Page ${data.pageNumber} of ${pageNumber}`, pageWidth - margin, doc.internal.pageSize.height - 10, { align: 'right' });
      }
    });

    doc.save(`${balanceSheet.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    setShowExportOptions(false);
  };

  // Export to CSV function
  const exportToCSV = () => {
    let csvContent = '';

    // Header
    csvContent += `${companyInfo?.name || "NO COMPANY NAME"}\n`;
    csvContent += `${balanceSheet}\n`;
    csvContent += `As of: ${format(new Date(), "MMMM dd, yyyy")}\n\n`;

    // Table headers
    const headers = ['Account', ...data.map(dataset => getMonthsInRange(dataset.date))];
    csvContent += headers.join(',') + '\n';

    // Process each category
    const categories = [
      { label: "Assets", type: "Asset", hasSubcategories: true },
      { label: "Liabilities", type: "Liability", hasSubcategories: true },
      { label: "Equity", type: "Equity", hasSubcategories: false }
    ];

    categories.forEach(category => {
      // Category header
      csvContent += category.label.toUpperCase() + ','.repeat(data.length) + '\n';

      if (category.hasSubcategories) {
        ["Current", "Non-Current"].forEach(subCategory => {
          const hasValues = data.some(dataset => {
            const accounts = categorizeAccounts(dataset.data, category.type, subCategory);
            return accounts.some(acc => acc.balance !== 0);
          });

          if (hasValues) {
            csvContent += `${subCategory} ${category.label}` + ','.repeat(data.length) + '\n';

            const accounts = categorizeAccounts(data[0]?.data || [], category.type, subCategory)
              .filter(account => account.balance !== 0);

            accounts.forEach(account => {
              const row = [
                account.accountName,
                ...data.map(dataset => {
                  const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
                  return matchingAccount ? matchingAccount.balance.toLocaleString() + ' Nu.' : '0 Nu.';
                })
              ];
              csvContent += row.join(',') + '\n';
            });

            const subtotalRow = [
              `Total ${subCategory} ${category.label}`,
              ...data.map(dataset => {
                const accounts = categorizeAccounts(dataset.data, category.type, subCategory);
                return calculateTotal(accounts).toLocaleString() + ' Nu.';
              })
            ];
            csvContent += subtotalRow.join(',') + '\n';
          }
        });
      } else {
        const accounts = categorizeAccounts(data[0]?.data || [], category.type)
          .filter(account => account.balance !== 0);

        accounts.forEach(account => {
          const row = [
            account.accountName,
            ...data.map(dataset => {
              const matchingAccount = dataset.data.find(acc => acc.accountName === account.accountName);
              return matchingAccount ? matchingAccount.balance.toLocaleString() + ' Nu.' : '0 Nu.';
            })
          ];
          csvContent += row.join(',') + '\n';
        });

        // Add Net Profit row under Equity in CSV
        if (category.type === "Equity" && netProfitByPeriod.length > 0) {
          const npRow = [
            'Net Profit (Retained Earnings)',
            ...netProfitByPeriod.map(np => np.toLocaleString() + ' Nu.')
          ];
          csvContent += npRow.join(',') + '\n';
        }
      }

      const totalRow = [
        `Total ${category.label}`,
        ...data.map((dataset, index) => {
          const accounts = categorizeAccounts(dataset.data, category.type);
          const accountTotal = calculateTotal(accounts);
          const netProfit = category.type === "Equity" ? (netProfitByPeriod[index] || 0) : 0;
          return (accountTotal + netProfit).toLocaleString() + ' Nu.';
        })
      ];
      csvContent += totalRow.join(',') + '\n';
    });

    // Final total row
    csvContent += '\n';
    const finalTotalRow = [
      'Total Liabilities + Equity',
      ...data.map((dataset, index) => {
        const liabilities = categorizeAccounts(dataset.data, "Liability");
        const equity = categorizeAccounts(dataset.data, "Equity");
        const netProfit = netProfitByPeriod[index] || 0;
        const total = calculateTotal(liabilities) + calculateTotal(equity) + netProfit;
        return total.toLocaleString() + ' Nu.';
      })
    ];
    csvContent += finalTotalRow.join(',') + '\n';

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${balanceSheet.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mt-4">
      <div className="mb-8 flex flex-col items-center justify-center">
        {companyInfo?.logo && (
          <img
            src={companyInfo.logo}
            alt="Company Logo"
            className="h-24 w-auto object-contain mb-4"
          />
        )}

        <h1 className="text-3xl font-extrabold text-gray-900 mb-2 text-center">
          {companyInfo ? companyInfo.name : "NO COMPANY NAME"}
        </h1>

        <div className="flex justify-center mb-2">
          <h2
            className="text-xl font-bold text-gray-700 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => setEditing(true)}
          >
            {editing ? (
              <input
                type="text"
                value={balanceSheet}
                onChange={(e) => setBalanceSheet(e.target.value)}
                onBlur={() => setEditing(false)}
                autoFocus
                className="border-b-2 border-gray-300 focus:outline-none focus:border-blue-500 text-center bg-transparent"
              />
            ) : (
              balanceSheet
            )}
          </h2>
        </div>

        <p className="text-center font-medium text-gray-500 text-sm">
          {format(new Date(), "MMMM dd, yyyy")}
        </p>

        {/* Export Button */}
        <div className="relative mt-4 flex justify-center">
          <button
            onClick={() => setShowExportOptions(!showExportOptions)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 font-medium"
          >
            <Download size={18} />
            Export Report
          </button>

          {/* Export Options Dropdown */}
          {showExportOptions && (
            <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[180px]">
              <button
                onClick={exportToPDF}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
              >
                <FileText size={16} className="text-blue-500" />
                <span>Export as PDF</span>
              </button>
              <button
                onClick={exportToCSV}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
              >
                <Table size={16} className="text-blue-600" />
                <span>Export as CSV</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-gray-700 font-semibold border-b">
              <th className="px-6 py-3 text-left">Category</th>
              {data.map((dataset, index) => (
                <th key={index} className="px-6 py-3 text-right">
                  {getMonthsInRange(dataset.date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { label: "Assets", type: "Asset", hasSubcategories: true },
              {
                label: "Liabilities",
                type: "Liability",
                hasSubcategories: true,
              },
              { label: "Equity", type: "Equity", hasSubcategories: false },
            ].map((category) => (
              <React.Fragment key={category.label}>
                <tr className="bg-blue-50">
                  <td
                    className="px-6 py-3 font-bold text-lg text-gray-900"
                    colSpan={data.length + 1}
                  >
                    {category.label}
                  </td>
                </tr>

                {category.hasSubcategories ? (
                  // For Assets and Liabilities with Current and Non-Current subcategories
                  ["Current", "Non-Current"].map((subCategory) => {
                    const hasValues = data.some((dataset) => {
                      const accounts = categorizeAccounts(
                        dataset.data,
                        category.type,
                        subCategory
                      );
                      return accounts.some((acc) => acc.balance !== 0);
                    });

                    if (!hasValues) return null;

                    return (
                      <React.Fragment key={subCategory}>
                        <tr>
                          <td
                            className="px-8 py-2 font-semibold text-blue-500"
                            colSpan={data.length + 1}
                          >
                            {subCategory} {category.label}
                          </td>
                        </tr>

                        {categorizeAccounts(
                          data[0]?.data || [],
                          category.type,
                          subCategory
                        )
                          .filter((account) => account.balance !== 0)
                          .map((account, accIndex) => (
                            <tr key={accIndex} className="hover:bg-gray-100">
                              <td
                                className="pl-12 py-2 text-gray-700 hover:text-blue-500 hover:underline cursor-pointer"
                                onClick={() => handleAccountClick(account)}
                              >
                                {account.accountName}
                              </td>
                              {data.map((dataset, index) => {
                                const matchingAccount = dataset.data.find(
                                  (acc) =>
                                    acc.accountName === account.accountName
                                );
                                return (
                                  <td
                                    key={index}
                                    className="px-6 py-2 text-right text-gray-800"
                                  >
                                    {matchingAccount
                                      ? matchingAccount.balance.toLocaleString()
                                      : "0"}{" "}
                                    Nu.
                                  </td>
                                );
                              })}
                            </tr>
                          ))}

                        <tr className="font-bold text-gray-800">
                          <td className="px-8 py-2">
                            Total {subCategory} {category.label}
                          </td>
                          {data.map((dataset, index) => {
                            const accounts = categorizeAccounts(
                              dataset.data,
                              category.type,
                              subCategory
                            );
                            return (
                              <td key={index} className="px-6 py-2 text-right">
                                {calculateTotal(accounts).toLocaleString()} Nu.
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })
                ) : (
                  // For Equity - display all equity accounts without subcategories + Net Profit
                  <>
                    {categorizeAccounts(data[0]?.data || [], category.type)
                      .filter((account) => account.balance !== 0)
                      .map((account, accIndex) => (
                        <tr key={accIndex} className="hover:bg-gray-100">
                          <td className="pl-12 py-2 text-gray-700 hover:text-blue-500 hover:underline cursor-pointer">
                            {account.accountName}
                          </td>
                          {data.map((dataset, index) => {
                            const matchingAccount = dataset.data.find(
                              (acc) => acc.accountName === account.accountName
                            );
                            return (
                              <td
                                key={index}
                                className="px-6 py-2 text-right text-gray-800"
                              >
                                {matchingAccount
                                  ? matchingAccount.balance.toLocaleString()
                                  : "0"}{" "}
                                Nu.
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    {/* Net Profit row under Equity */}
                    {netProfitByPeriod.length > 0 && (
                      <tr className="hover:bg-green-50">
                        <td className="pl-12 py-2 text-green-700 font-semibold">
                          Net Profit (Retained Earnings)
                        </td>
                        {netProfitByPeriod.map((netProfit, index) => (
                          <td
                            key={index}
                            className={`px-6 py-2 text-right font-semibold ${
                              netProfit >= 0 ? "text-green-700" : "text-red-600"
                            }`}
                          >
                            {netProfit.toLocaleString()} Nu.
                          </td>
                        ))}
                      </tr>
                    )}
                  </>
                )}

                <tr className="text-blue-400 font-bold">
                  <td className="px-6 py-2">Total {category.label}</td>
                  {data.map((dataset, index) => {
                    const accounts = categorizeAccounts(
                      dataset.data,
                      category.type
                    );
                    const accountTotal = calculateTotal(accounts);
                    const netProfit = category.type === "Equity" ? (netProfitByPeriod[index] || 0) : 0;
                    return (
                      <td key={index} className="px-6 py-2 text-right">
                        {(accountTotal + netProfit).toLocaleString()} Nu.
                      </td>
                    );
                  })}
                </tr>
              </React.Fragment>
            ))}
            <tr className="text-blue-500 font-semibold bg-gray-100">
              <td className="px-6 py-2 font-bold">
                Total Liabilities + Equity
              </td>
              {data.map((dataset, index) => {
                const liabilities = categorizeAccounts(
                  dataset.data,
                  "Liability"
                );
                const equity = categorizeAccounts(dataset.data, "Equity");
                const netProfit = netProfitByPeriod[index] || 0;
                const total =
                  calculateTotal(liabilities) + calculateTotal(equity) + netProfit;
                return (
                  <td key={index} className="px-6 py-2 text-right">
                    {total.toLocaleString()} Nu.
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Click outside to close export options */}
      {showExportOptions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowExportOptions(false)}
        />
      )}
    </div>
  );
};

export default BalanceSheetTable;